import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Typography,
  FormControlLabel,
  MenuItem,
  TextField
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowDropDown, ArrowDropUp, Search } from "@mui/icons-material";
import AlphabetFilter from "../../../components/AlphabetFilter/AlphabetFilter";
import { fetchCoursesFilterData } from "../../../redux/actions/contentCoursesActions";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";
import { enterKeyCode, courseStatuses } from "../../../constants";
import usePermissions from "../../../redux/selectors/usePermissions";

const initialFiltersState = {
  status: courseStatuses[0],
  contentPackage: "",
  hideAdditionalContent: false,
  startLetter: "",
  searchInput: ""
};

const useStyles = makeStyles(theme => ({
  filterContainer: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    flexWrap: "wrap",
    "& > div": {
      margin: theme.spacing(0, 3, 2, 0),
      marginRight: theme.spacing(3),
      maxWidth: 321
    }
  },
  menuItem: {
    color: theme.palette.text.secondary
  }
}));

const ExpandableFilterMenu = ({
  expanded,
  hideAdditionalContent,
  onHideAdditionalContentChange,
  status,
  statusOptions,
  onStatusChange,
  contentPackage,
  onContentPackageChange,
  contentPackageOptions,
  onFilterList,
  onClearFilters
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isInstructor } = usePermissions();

  return (
    <Collapse in={expanded}>
      <Box display="flex" className={classes.filterContainer}>
        <TextField
          select
          value={status}
          onChange={onStatusChange}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            inputProps: { "data-qa": "course-status-filter" }
          }}
          fullWidth
          SelectProps={{
            displayEmpty: true,
            renderValue: currentOption => {
              const selectedOption = statusOptions.find(
                option => option.value === currentOption.value
              );
              return `${t("manageCourses.coursesStatusFilter")}: ${
                selectedOption.value !== courseStatuses[0].value
                  ? t(
                      statusOptions.find(
                        option => option.value === currentOption.value
                      ).label
                    )
                  : t("manageCourses.coursesFilterShowAllOption")
              }`;
            }
          }}
          hiddenLabel
        >
          {statusOptions.map(option => (
            <MenuItem value={option} key={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          value={contentPackage}
          onChange={onContentPackageChange}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            inputProps: { "data-qa": "content-package-filter" }
          }}
          fullWidth
          SelectProps={{
            displayEmpty: true,
            renderValue: value =>
              `${t("manageCourses.contentPackageFilter")}: ${
                value && contentPackageOptions
                  ? contentPackageOptions.find(option => option.value === value)
                      .label
                  : t("manageCourses.coursesFilterShowAllOption")
              }`
          }}
          hiddenLabel
        >
          <MenuItem value="" key="all">
            {t("manageCourses.coursesFilterShowAllOption")}
          </MenuItem>
          {!!contentPackageOptions &&
            contentPackageOptions.map(option => (
              <MenuItem
                value={option.value}
                key={option.value}
                className={option.hasAccess ? null : classes.menuItem}
              >
                {option.label}
              </MenuItem>
            ))}
        </TextField>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: "column", sm: "row" }}
        ml={1}
      >
        {isInstructor ? (
          <Box mr={3}></Box>
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                checked={hideAdditionalContent}
                onChange={() =>
                  onHideAdditionalContentChange(!hideAdditionalContent)
                }
                value="hideAdditionalContent"
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t("manageCourses.coursesHideAdditionalContentCheckbox")}
              </Typography>
            }
          />
        )}

        <Box display="flex" justifyContent="flex-end" my={{ xs: 3, sm: 0 }}>
          <Box mr={3}>
            <Button color="primary" onClick={onClearFilters}>
              {t("manageCourses.coursesClearFiltersButton")}
            </Button>
          </Box>
          <Box>
            <Button variant="contained" color="primary" onClick={onFilterList}>
              {t("manageCourses.coursesFiltersButton")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
};

const CoursesFilters = ({
  onApplyFilters,
  defaultValues,
  expandedByDefault
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    coursesFilterData: { contentPackages }
  } = useSelector(state => state.contentCoursesState);

  const [filterMenuExpanded, setFilterMenuExpanded] = useState(
    expandedByDefault
  );
  const [currentFilterInput, setCurrentFilterInput] = useState(defaultValues);

  const handleFilterInputChange = field => event => {
    setCurrentFilterInput({
      ...currentFilterInput,
      [field]: event && event.target ? event.target.value : event
    });
  };

  const handleSetFilterMenuExpanded = () => {
    setFilterMenuExpanded(!filterMenuExpanded);
  };

  /**
   * If the filter state is not equal to the initial filter state,
   * clear the filters and send a request to get unfiltered data
   */
  const handleClearFilters = () => {
    onApplyFilters(initialFiltersState);
    setCurrentFilterInput(initialFiltersState);
  };

  /**
   * Called when the user clicks on
   * input as well as the currently applied filters
   */
  const handleStartLetterChange = startLetter => {
    setCurrentFilterInput({
      ...currentFilterInput,
      startLetter
    });
    onApplyFilters({
      startLetter
    });
  };

  /**
   * Called when the user presses the enter key to submit the search
   * input as well as the currently applied filters
   */
  const handleSearchSubmit = () => {
    onApplyFilters({
      searchInput: currentFilterInput.searchInput
    });
  };

  const handleFilterList = () => {
    onApplyFilters({ ...currentFilterInput });
  };
  useEffect(() => {
    dispatch(fetchCoursesFilterData());
  }, []);

  return (
    <>
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} mt={3}>
        <TextField
          fullWidth
          value={currentFilterInput.searchInput}
          onChange={handleFilterInputChange("searchInput")}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            endAdornment: <Search color="action" />,
            onKeyDown: ({ keyCode }) =>
              keyCode === enterKeyCode && handleSearchSubmit(),
            inputProps: { "data-qa": "courses-search" }
          }}
          placeholder={t("manageCourses.coursesSearchPlaceholder")}
          hiddenLabel
        />
        <Box
          display="flex"
          justifyContent={{ xs: "flex-end", sm: "flex-start" }}
          alignItems="center"
          ml={{ xs: 0, sm: 7 }}
          mt={{ xs: 2, sm: 0 }}
        >
          <Button
            data-qa="filter-drop-down"
            color="primary"
            endIcon={filterMenuExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
            onClick={handleSetFilterMenuExpanded}
          >
            {t("manageCourses.coursesFiltersDropdown")}
          </Button>
        </Box>
      </Box>
      <ExpandableFilterMenu
        status={currentFilterInput.status}
        statusOptions={courseStatuses}
        expanded={filterMenuExpanded}
        onHideAdditionalContentChange={handleFilterInputChange(
          "hideAdditionalContent"
        )}
        hideAdditionalContent={currentFilterInput.hideAdditionalContent}
        contentPackageOptions={contentPackages}
        contentPackage={currentFilterInput.contentPackage}
        onContentPackageChange={handleFilterInputChange("contentPackage")}
        onStatusChange={handleFilterInputChange("status")}
        onFilterList={handleFilterList}
        onClearFilters={handleClearFilters}
      />
      <Box mt={4} mb={2} mx={2}>
        <AlphabetFilter
          value={currentFilterInput.startLetter}
          onChange={handleStartLetterChange}
        />
      </Box>
    </>
  );
};

export default CoursesFilters;

CoursesFilters.propTypes = {
  /**
   * Called when the user presses the enter key on the search input box, clicks on one of the starting letter options,
   * or clicks on the "Filter List" button
   */
  onApplyFilters: PropTypes.func
};
